/**
 * Known Accounts — application shell.
 *
 * UI sprint (2026-05-11): rebased onto Wayne's Brain pwa-v2 design system.
 *   - `.v2-shell` flex layout (sidebar on desktop, full-width on mobile)
 *   - `Sidebar` chrome with grouped nav + branded mark + ⌘K search hint
 *   - `Topbar` with route breadcrumb + Live indicator
 *   - `MobileTabbar` bottom tabs (hidden on desktop via CSS)
 *
 * Existing screens render inside `.v2-container` and read pwa-v2's legacy
 * token aliases, so they keep working through this shell migration while
 * each gets a proper redesign in later sprint steps.
 */
import type { Clerk } from '@clerk/clerk-js';
import { LocationProvider, Route, Router } from 'preact-iso';
import { useEffect, useRef, useState } from 'preact/hooks';
import { type MeResponse, createCheckoutSession, getMe, restoreEntity } from './api.ts';
import { loadClerk } from './clerk.ts';
import { MobileTabbar, Sidebar, Topbar } from './components/chrome';
import { Board } from './screens/Board.tsx';
import { Briefing } from './screens/Briefing.tsx';
import { Capture } from './screens/Capture.tsx';
import { EntityDetail } from './screens/EntityDetail.tsx';
import { Onboarding } from './screens/Onboarding.tsx';
import { Portfolio } from './screens/Portfolio.tsx';
import { Search } from './screens/Search.tsx';
import { Settings } from './screens/Settings.tsx';

type LoadState = 'loading' | 'signed_out' | 'signed_in' | 'error';

export function App() {
  const [clerk, setClerk] = useState<Clerk | null>(null);
  const [state, setState] = useState<LoadState>('loading');
  const [me, setMe] = useState<MeResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [redirecting, setRedirecting] = useState(false);
  const autoRedirectFired = useRef(false);

  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    loadClerk()
      .then((c) => {
        setClerk(c);
        setState(c.user ? 'signed_in' : 'signed_out');
        unsubscribe = c.addListener(({ user }) => {
          setState(user ? 'signed_in' : 'signed_out');
          if (!user) setMe(null);
        });
      })
      .catch((err: Error) => {
        setError(err.message);
        setState('error');
      });
    return () => unsubscribe?.();
  }, []);

  useEffect(() => {
    if (state !== 'signed_in') return;
    let cancelled = false;
    let attempts = 0;
    const MAX_ATTEMPTS = 18;

    const tick = async () => {
      if (cancelled) return;
      try {
        const result = await getMe();
        if (cancelled) return;
        setMe(result);
        const stillTransient =
          result.is_provisioning || result.tenant.subscription_status === 'incomplete';
        if (stillTransient && attempts < MAX_ATTEMPTS) {
          attempts += 1;
          const delayMs = result.is_provisioning ? 2_000 : 10_000;
          setTimeout(tick, delayMs);
        }
      } catch (err) {
        if (!cancelled) setError((err as Error).message);
      }
    };
    tick();
    return () => {
      cancelled = true;
    };
  }, [state]);

  // One-shot /api/me refetch — used by the onboarding wizard's onComplete so
  // the now-set `industry_template` flips the app into normal rendering
  // without a full page reload.
  const refetchMe = async () => {
    try {
      const result = await getMe();
      setMe(result);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  useEffect(() => {
    if (autoRedirectFired.current) return;
    if (!me) return;
    if (me.is_provisioning) return;
    if (me.tenant.plan_tier === 'founder') return;
    if (me.tenant.subscription_status !== 'incomplete') return;

    const params = new URLSearchParams(window.location.search);
    if (params.get('incomplete') === '1') return;

    const isDp = params.get('dp') === '1';
    autoRedirectFired.current = true;
    setRedirecting(true);
    createCheckoutSession({ dp: isDp })
      .then(({ url }) => {
        window.location.href = url;
      })
      .catch((err: Error) => {
        autoRedirectFired.current = false;
        setRedirecting(false);
        setError(`Couldn't start Checkout: ${err.message}`);
      });
  }, [me]);

  if (state === 'loading') {
    return <BareScreen>Loading…</BareScreen>;
  }
  if (state === 'error') {
    return (
      <BareScreen>
        <pre class="error-banner">{error}</pre>
      </BareScreen>
    );
  }
  if (state === 'signed_out') {
    const here = window.location.href;
    return (
      <BareScreen>
        <h2
          style={{
            fontFamily: 'var(--f-serif)',
            fontStyle: 'italic',
            fontSize: 32,
            fontWeight: 400,
            marginBottom: 12,
          }}
        >
          Known Accounts
        </h2>
        <p class="muted" style={{ marginBottom: 24 }}>
          Sign in to your workspace.
        </p>
        <button
          type="button"
          class="btn btn-primary"
          onClick={() =>
            clerk?.redirectToSignIn({
              signInForceRedirectUrl: here,
              signUpForceRedirectUrl: here,
            })
          }
        >
          Sign in
        </button>
      </BareScreen>
    );
  }

  // Onboarding wizard gate (onboarding.md §1.2 / §12.1): a signed-in tenant
  // with full access but no `industry_template` set hasn't finished the
  // application wizard. Render it outside the Shell — the wizard is its own
  // full-bleed surface, not a route inside the app chrome.
  if (
    me &&
    !me.is_provisioning &&
    tenantHasFullAccess(me.tenant.subscription_status) &&
    me.tenant.industry_template === null
  ) {
    return <Onboarding onComplete={refetchMe} workspaceName={me.user.name ?? me.tenant.name} />;
  }

  return (
    <LocationProvider>
      <Shell clerk={clerk} me={me}>
        {me && me.tenant.subscription_status === 'incomplete' && !me.is_provisioning ? (
          <IncompleteState redirecting={redirecting} error={error} />
        ) : !me ? (
          <p class="empty">Loading workspace…</p>
        ) : tenantHasFullAccess(me.tenant.subscription_status) ? (
          <AppRoutes me={me} />
        ) : (
          <p class="empty">Setting up…</p>
        )}
      </Shell>
    </LocationProvider>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Shell — v2 layout: sidebar (desktop) + main column (topbar + content +
// mobile tabbar).
// ───────────────────────────────────────────────────────────────────────────

function Shell({
  clerk,
  me,
  children,
}: {
  clerk: Clerk | null;
  me: MeResponse | null;
  children: preact.ComponentChildren;
}) {
  return (
    <div class="v2-shell">
      <Sidebar clerk={clerk} tenantName={me?.tenant.name ?? null} />
      <div class="v2-main">
        <Topbar />
        <div class="main-scroll">
          <div class="container v2-container">{children}</div>
        </div>
        <MobileTabbar />
      </div>
    </div>
  );
}

function BareScreen({ children }: { children: preact.ComponentChildren }) {
  return (
    <div
      style={{
        minHeight: '100vh',
        background: 'var(--bg-canvas)',
        color: 'var(--text)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <div style={{ maxWidth: 360, width: '100%' }}>{children}</div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Routes
// ───────────────────────────────────────────────────────────────────────────

function AppRoutes({ me }: { me: MeResponse }) {
  const [refreshKey, setRefreshKey] = useState(0);
  const bumpRefresh = () => setRefreshKey((k) => k + 1);
  // Post-delete restore toast. Survives navigation away from the
  // entity-detail page (where the delete fired) so the Undo affordance
  // is reachable on Portfolio / Briefing. Auto-dismisses after 8s —
  // longer than the standard undo because navigation overhead.
  const [deletedRecent, setDeletedRecent] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [restoreError, setRestoreError] = useState<string | null>(null);

  useEffect(() => {
    if (!deletedRecent) return;
    const t = setTimeout(() => setDeletedRecent(null), 8000);
    return () => clearTimeout(t);
  }, [deletedRecent]);

  useEffect(() => {
    if (!restoreError) return;
    const t = setTimeout(() => setRestoreError(null), 5000);
    return () => clearTimeout(t);
  }, [restoreError]);

  async function handleUndoDelete() {
    if (!deletedRecent) return;
    const target = deletedRecent;
    try {
      await restoreEntity(target.id);
      setDeletedRecent(null);
      bumpRefresh();
      navigateTo(`/entity/${encodeURIComponent(target.id)}`);
    } catch (err) {
      setRestoreError((err as Error).message);
    }
  }

  return (
    <>
      <Router>
        <Route
          path="/"
          component={() => (
            <Briefing me={me} refreshKey={refreshKey} onSelectEntity={navigateToEntity} />
          )}
        />
        <Route
          path="/capture"
          component={() => <Capture tenantId={me.tenant.id} onSaved={() => bumpRefresh()} />}
        />
        <Route
          path="/portfolio"
          component={() => <Portfolio refreshKey={refreshKey} onSelectEntity={navigateToEntity} />}
        />
        <Route path="/board" component={() => <Board />} />
        <Route path="/search" component={() => <Search onSelectEntity={navigateToEntity} />} />
        <Route path="/settings/advanced" component={() => <Settings me={me} />} />
        <Route path="/dp-convert" component={DpConvert} />
        <Route
          path="/entity/:id"
          component={(props: { id?: string }) => (
            <EntityRoute
              id={props.id}
              onDeletedWithName={(id, name) => {
                setDeletedRecent({ id, name });
                navigateTo('/portfolio');
              }}
            />
          )}
        />
        <Route default component={NotFound} />
      </Router>
      {deletedRecent && (
        <div class="undo-toast">
          <span>
            Deleted <strong>{deletedRecent.name}</strong>
          </span>
          <button type="button" onClick={handleUndoDelete}>
            Undo
          </button>
        </div>
      )}
      {restoreError && (
        <div
          class="undo-toast"
          style={{ background: 'var(--danger-tint)', color: 'var(--danger)' }}
        >
          <span>Restore failed: {restoreError}</span>
          <button
            type="button"
            onClick={() => setRestoreError(null)}
            style={{ color: 'var(--danger)' }}
          >
            Dismiss
          </button>
        </div>
      )}
    </>
  );
}

function EntityRoute(props: {
  id?: string;
  onDeletedWithName: (id: string, name: string) => void;
}) {
  const id = props.id;
  if (!id) return <p class="empty">No entity id in URL.</p>;

  return (
    <EntityDetail
      entityId={id}
      onBack={() => window.history.back()}
      onSelectEntity={navigateToEntity}
      onDeleted={(deletedId, deletedName) =>
        props.onDeletedWithName(deletedId, deletedName ?? 'entity')
      }
    />
  );
}

function NotFound() {
  return (
    <div class="empty">
      <p>Page not found.</p>
      <a href="/" class="btn btn-ghost" style={{ marginTop: 12, display: 'inline-flex' }}>
        Back to Briefing
      </a>
    </div>
  );
}

/**
 * /dp-convert?coupon=… — the design-partner month-6 conversion landing
 * (pricing §8). Wayne sends a still-in-trial DP a personal link; this route
 * reads the coupon, creates a Checkout Session with it pre-applied (card
 * required — the discounted total is non-zero), and redirects to Stripe.
 *
 * The coupon itself is created manually in the Stripe Dashboard (§8 —
 * programmatic coupon creation is explicit Phase 1.x "Eventual automation").
 * The post-auto-cancel conversion path (subscription_status='canceled', so
 * this route doesn't render inside AppRoutes) is lifecycle §9 reactivation
 * territory — pairs with Phase 2 enforcement, not B.7.
 */
function DpConvert() {
  const [error, setError] = useState<string | null>(null);
  const firedRef = useRef(false);

  useEffect(() => {
    if (firedRef.current) return;
    firedRef.current = true;
    const coupon = new URLSearchParams(window.location.search).get('coupon');
    if (!coupon) {
      setError('This conversion link is missing its coupon code.');
      return;
    }
    createCheckoutSession({ coupon })
      .then(({ url }) => {
        window.location.href = url;
      })
      .catch((err: Error) => {
        setError(`Couldn't start checkout: ${err.message}`);
      });
  }, []);

  return (
    <div class="empty">
      {error ? (
        <>
          <pre class="error-banner">{error}</pre>
          <a href="/" class="btn btn-ghost" style={{ marginTop: 12, display: 'inline-flex' }}>
            Back to Briefing
          </a>
        </>
      ) : (
        <p>Taking you to checkout…</p>
      )}
    </div>
  );
}

function navigateTo(path: string) {
  window.history.pushState(null, '', path);
  dispatchEvent(new PopStateEvent('popstate'));
}

function navigateToEntity(id: string) {
  navigateTo(`/entity/${encodeURIComponent(id)}`);
}

// ───────────────────────────────────────────────────────────────────────────
// Lifecycle states
// ───────────────────────────────────────────────────────────────────────────

function tenantHasFullAccess(status: string): boolean {
  return status === 'trialing' || status === 'active' || status === 'past_due' || status === 'comp';
}

function IncompleteState({ redirecting, error }: { redirecting: boolean; error: string | null }) {
  const params = new URLSearchParams(window.location.search);
  const bailed = params.get('incomplete') === '1';
  const isDp = params.get('dp') === '1';
  const [retrying, setRetrying] = useState(false);
  const [retryErr, setRetryErr] = useState<string | null>(null);

  const retry = async () => {
    setRetrying(true);
    setRetryErr(null);
    try {
      const { url } = await createCheckoutSession({ dp: isDp });
      window.location.href = url;
    } catch (e: unknown) {
      setRetryErr((e as Error).message);
      setRetrying(false);
    }
  };

  if (bailed) {
    return (
      <section class="stack-lg">
        <h2>Pick up where you left off</h2>
        <p class="muted">You closed Checkout before finishing — no charge, no problem.</p>
        <button type="button" class="btn btn-primary" disabled={retrying} onClick={retry}>
          {retrying ? 'Redirecting…' : 'Resume signup'}
        </button>
        {retryErr && <pre class="error-banner">{retryErr}</pre>}
      </section>
    );
  }

  if (error) {
    return (
      <section class="stack-lg">
        <pre class="error-banner">{error}</pre>
        <button type="button" class="btn btn-primary" disabled={retrying} onClick={retry}>
          {retrying ? 'Retrying…' : 'Try again'}
        </button>
        {retryErr && <pre class="error-banner">{retryErr}</pre>}
      </section>
    );
  }

  return (
    <section class="stack-lg">
      <p class="muted">
        {redirecting ? 'Redirecting to Stripe…' : 'Setting up your subscription…'}
      </p>
    </section>
  );
}
